import type { Messages } from '../i18n/messages';

/**
 * Admin module message catalog (Task 7f of the Fun Mode string sweep). Covers
 * admin/ (AdminPage, AdminLayout, SharedActionsVersionView), admin/dashboard,
 * admin/assignments, and admin/cron. Server/dynamic data (owner names, service
 * names, metric counts, entity refs, roster member names, cron job ids/
 * timestamps/log output) is never keyed — only interpolated via `{param}`.
 * "Merge Lord" is intentionally already fun and keeps identical `en`/`fun`,
 * as do other brand/product names (ArgoCD, Sentry, SonarQube, Kafka, Kubernetes).
 */
export const adminMessages: Messages = {
  // ---- AdminLayout.tsx ----
  'admin.layout.ariaLabel': { en: 'Admin sections', fun: 'Admin sections' },

  // ---- AdminPage.tsx ----
  'admin.page.title': { en: 'Admin', fun: 'Mission Control' },
  'admin.page.description': {
    en: 'Administer shared platform settings.',
    fun: 'Pull the levers behind the curtain.',
  },
  'admin.sections.dashboard.label': { en: 'Dashboard', fun: 'Dashboard' },
  'admin.sections.dashboard.description': {
    en: 'Org-wide service health',
    fun: 'The whole org, vitals and all',
  },
  'admin.sections.platformHealth.label': {
    en: 'Platform Health',
    fun: 'Vitals',
  },
  'admin.sections.platformHealth.description': {
    en: 'Backstage process, storage & cache health',
    fun: "Backstage's own pulse, storage & stashes",
  },
  'admin.sections.dora.label': { en: 'DORA', fun: 'DORA' },
  'admin.sections.dora.description': {
    en: 'Fleet delivery performance',
    fun: 'How fast the fleet ships',
  },
  'admin.sections.sharedActions.label': {
    en: 'Shared Actions',
    fun: 'Shared Actions',
  },
  'admin.sections.sharedActions.description': {
    en: 'Release version pointer',
    fun: 'The version dial',
  },
  'admin.sections.assignments.label': { en: 'Assignments', fun: 'Roster' },
  'admin.sections.assignments.description': {
    en: 'Character rosters',
    fun: 'Who is wearing the crown',
  },
  'admin.sections.cronJobs.label': { en: 'Cron jobs', fun: 'Cron jobs' },
  'admin.sections.cronJobs.description': {
    en: 'Scheduled jobs & runs',
    fun: 'Robots on a schedule',
  },
  'admin.accessDenied.title': { en: 'Access denied', fun: 'Nice try' },
  'admin.accessDenied.body': {
    en: 'You do not have access to this page. It requires membership in {group}.',
    fun: 'This page is VIP-only. You will need to be in {group} to get past the rope.',
  },
  'admin.versionUpdate.successTitle': {
    en: 'Version update started',
    fun: 'Version bump away!',
  },
  'admin.versionUpdate.viewTaskAction': { en: 'View task', fun: 'Watch it go' },
  'admin.versionUpdate.failTitle': {
    en: 'Submission failed',
    fun: 'That did not stick',
  },

  // ---- SharedActionsVersionView.tsx ----
  'admin.sharedActions.cardTitle': {
    en: 'Shared Actions release version',
    fun: 'Shared Actions release version',
  },
  'admin.sharedActions.refreshAriaLabel': { en: 'Refresh', fun: 'Refresh' },
  'admin.sharedActions.descriptionPrefix': {
    en: 'Reads and updates ',
    fun: 'Peeks at and pokes ',
  },
  'admin.sharedActions.descriptionSuffix': {
    en: '. Saving runs the update task — admins commit directly; others create an approval.',
    fun: '. Hitting save kicks off the update task — admins push straight through, everyone else files an approval.',
  },
  'admin.sharedActions.loadingVersion': {
    en: 'Loading current version…',
    fun: 'Sniffing out the current version…',
  },
  'admin.sharedActions.currentReleaseLabel': {
    en: 'Current release:',
    fun: 'Currently running:',
  },
  'admin.sharedActions.newVersionLabel': {
    en: 'New release version',
    fun: 'New release version',
  },
  'admin.sharedActions.newVersionPlaceholder': {
    en: 'e.g. 2604',
    fun: 'e.g. 2604',
  },
  'admin.sharedActions.invalidError': {
    en: 'Enter a positive integer',
    fun: 'Needs to be a real, positive whole number',
  },
  'admin.sharedActions.saveButton': { en: 'Save', fun: 'Ship it' },
  'admin.sharedActions.savingButton': { en: 'Saving…', fun: 'Shipping…' },

  // ---- dashboard/AdminDashboard.tsx (connected) ----
  'admin.dashboard.metric.ci': { en: 'CI failed', fun: 'CI faceplanted' },
  'admin.dashboard.metric.argocd': { en: 'Out of sync', fun: 'Out of step' },
  'admin.dashboard.metric.services': {
    en: 'Services failed',
    fun: 'Services down for the count',
  },
  'admin.dashboard.metric.sentry': { en: 'Sentry issues', fun: 'Sentry fires' },
  'admin.dashboard.metric.security': {
    en: 'Security alerts',
    fun: 'Red flags',
  },
  'admin.dashboard.metric.quality': {
    en: 'Quality gates failed',
    fun: 'Gates slammed shut',
  },
  'admin.dashboard.metric.kafka': {
    en: 'Kafka lagging',
    fun: 'Kafka dragging',
  },
  'admin.dashboard.metricTab.ci': { en: 'CI/CD', fun: 'CI/CD' },
  'admin.dashboard.metricTab.argocd': { en: 'ArgoCD', fun: 'ArgoCD' },
  'admin.dashboard.metricTab.services': { en: 'Kubernetes', fun: 'Kubernetes' },
  'admin.dashboard.metricTab.sentry': { en: 'Sentry', fun: 'Sentry' },
  'admin.dashboard.metricTab.security': {
    en: 'Security',
    fun: 'Lock It Down',
  },
  'admin.dashboard.metricTab.quality': { en: 'SonarQube', fun: 'SonarQube' },
  'admin.dashboard.metricTab.kafka': { en: 'Kafka', fun: 'Kafka' },
  'admin.dashboard.envMetric.outOfSync': {
    en: 'Out of Sync',
    fun: 'Out of Step',
  },
  'admin.dashboard.envMetric.servicesFailed': {
    en: 'Services Failed',
    fun: 'Services Down',
  },
  'admin.dashboard.envMetric.kafka': { en: 'Kafka', fun: 'Kafka' },
  'admin.dashboard.envMetric.sentry': { en: 'Sentry', fun: 'Sentry' },
  'admin.dashboard.snack.showingMetric': {
    en: 'Showing {metric} details',
    fun: 'Zooming into {metric}',
  },
  'admin.dashboard.snack.showingOwner': {
    en: 'Showing issues for {owner}',
    fun: 'Putting {owner} under the microscope',
  },
  'admin.dashboard.snack.filterApplied': {
    en: 'Filter applied',
    fun: 'Filter locked in',
  },
  'admin.dashboard.snack.viewDetailsAction': {
    en: 'View details',
    fun: 'Peek at details',
  },
  'admin.dashboard.detail.ciFailed': {
    en: '{run} — failed',
    fun: '{run} — went down swinging',
  },
  'admin.dashboard.fallback.unknownRun': {
    en: 'Unknown run',
    fun: 'Mystery run',
  },
  'admin.dashboard.detail.argocdOutOfSyncEnv': {
    en: '{app} · out of sync ({env})',
    fun: '{app} · out of step ({env})',
  },
  'admin.dashboard.detail.argocdOutOfSync': {
    en: '{app} · out of sync',
    fun: '{app} · out of step',
  },
  'admin.dashboard.fallback.unknown': { en: 'unknown', fun: 'a mystery' },
  'admin.dashboard.reason.degraded': { en: 'Degraded', fun: 'Wobbling' },
  'admin.dashboard.reason.podFailed': { en: 'Pod failed', fun: 'Pod down' },
  'admin.dashboard.detail.servicesReasonEnv': {
    en: '{reason} ({env})',
    fun: '{reason} ({env})',
  },
  'admin.dashboard.detail.servicesReason': {
    en: '{reason}',
    fun: '{reason}',
  },
  'admin.dashboard.fallback.sentryIssue': {
    en: 'Sentry issue',
    fun: 'Sentry fire',
  },
  'admin.dashboard.fallback.unknownPackage': {
    en: 'unknown package',
    fun: 'a mystery package',
  },
  'admin.dashboard.detail.securityPkgSev': {
    en: '{pkg} ({sev})',
    fun: '{pkg} ({sev})',
  },
  'admin.dashboard.detail.qualityStatus': {
    en: '{project} — {status}',
    fun: '{project} — {status}',
  },
  'admin.dashboard.detail.kafkaLag': {
    en: '{group} · lag {lag}',
    fun: '{group} · dragging {lag}',
  },
  'admin.dashboard.combinedType.ci': { en: 'CI', fun: 'CI' },
  'admin.dashboard.combinedType.argocd': {
    en: 'Out of sync',
    fun: 'Out of step',
  },
  'admin.dashboard.combinedType.services': {
    en: 'Services',
    fun: 'Services',
  },
  'admin.dashboard.combinedType.sentry': { en: 'Sentry', fun: 'Sentry' },
  'admin.dashboard.combinedType.security': {
    en: 'Security',
    fun: 'Lock It Down',
  },
  'admin.dashboard.combinedType.quality': { en: 'Quality', fun: 'Quality' },
  'admin.dashboard.combinedType.kafka': { en: 'Kafka', fun: 'Kafka' },
  'admin.dashboard.detail.issuesForOwner': {
    en: 'Issues for {owner}',
    fun: 'The rap sheet for {owner}',
  },
  'admin.dashboard.detail.metricEnvTitle': {
    en: '{metric} — {env}',
    fun: '{metric} — {env}',
  },

  // ---- dashboard/AdminDashboardView.tsx ----
  'admin.dashboardView.title': { en: 'Service Health', fun: 'Vitals Check' },
  'admin.dashboardView.subtitle': {
    en: 'Org-wide health across all catalog services',
    fun: "Everybody's vitals, org-wide",
  },
  'admin.dashboardView.overviewLabel': { en: 'Overview', fun: 'Rundown' },
  'admin.dashboardView.trendsLabel': { en: 'Trends', fun: 'Vibes' },
  'admin.dashboardView.envChart.title': {
    en: 'Issues by Environment',
    fun: 'Issues by Environment',
  },
  'admin.dashboardView.envChart.description': {
    en: 'Active issues broken down by deployment environment',
    fun: 'Where the fires are burning, by environment',
  },
  'admin.dashboardView.healthChart.title': {
    en: 'Health Status',
    fun: 'Health Status',
  },
  'admin.dashboardView.healthChart.description': {
    en: 'Overall health status across all services',
    fun: 'The big picture, all services',
  },
  'admin.dashboardView.ownerChart.title': {
    en: 'Issues by Owner',
    fun: 'Issues by Owner',
  },
  'admin.dashboardView.ownerChart.description': {
    en: 'Total open issues per team owner',
    fun: 'Who is holding the most hot potatoes',
  },
  'admin.dashboardView.detailsLabel': { en: 'Details', fun: 'Fine Print' },

  // ---- dashboard/ApplicabilityChip.tsx ----
  'admin.applicabilityChip.envNa': {
    en: 'environment N/A',
    fun: 'environment: shrug',
  },
  'admin.applicabilityChip.allTime': { en: 'all-time', fun: 'all-time' },

  // ---- dashboard/ByEnvironmentChart.tsx ----
  'admin.envChart.notApplicable': {
    en: 'Not environment-specific',
    fun: 'Not an environment thing',
  },

  // ---- dashboard/DashboardFilterBar.tsx ----
  'admin.filterBar.allOwnersPlaceholder': {
    en: 'All owners',
    fun: 'Everybody',
  },
  'admin.filterBar.searchOwnersPlaceholder': {
    en: 'Search owners…',
    fun: 'Hunt for an owner…',
  },
  'admin.filterBar.environmentLabel': { en: 'Environment', fun: 'Environment' },
  'admin.filterBar.env.all': { en: 'All environments', fun: 'Everywhere' },
  'admin.filterBar.env.development': { en: 'Development', fun: 'Development' },
  'admin.filterBar.env.staging': { en: 'Staging', fun: 'Staging' },
  'admin.filterBar.env.preProd': { en: 'Pre-prod', fun: 'Pre-prod' },
  'admin.filterBar.env.production': { en: 'Production', fun: 'Production' },
  'admin.filterBar.rangeLabel': { en: 'Range', fun: 'Range' },
  'admin.filterBar.range.24h': { en: 'Last 24h', fun: 'Last 24h' },
  'admin.filterBar.range.2d': { en: 'Last 2 days', fun: 'Last 2 days' },
  'admin.filterBar.range.7d': { en: 'Last 7 days', fun: 'Last 7 days' },
  'admin.filterBar.range.14d': { en: 'Last 14 days', fun: 'Last 14 days' },
  'admin.filterBar.range.30d': { en: 'Last 30 days', fun: 'Last 30 days' },
  'admin.filterBar.updatedAt': {
    en: 'updated {time}',
    fun: 'freshened at {time}',
  },
  'admin.filterBar.updatedAtFallback': {
    en: 'updated …',
    fun: 'freshened …',
  },
  'admin.filterBar.staleBadge': { en: 'stale', fun: 'stale' },
  'admin.filterBar.refreshAriaLabel': { en: 'Refresh', fun: 'Refresh' },

  // ---- dashboard/DetailsPanel.tsx ----
  'admin.detailsPanel.recordCountOne': { en: '1 record', fun: '1 record' },
  'admin.detailsPanel.recordCountMany': {
    en: '{count} records',
    fun: '{count} records',
  },
  'admin.detailsPanel.allEnvironmentsOption': {
    en: 'All environments',
    fun: 'Everywhere',
  },
  'admin.detailsPanel.environmentLabel': {
    en: 'Environment',
    fun: 'Environment',
  },
  'admin.detailsPanel.allSeveritiesOption': {
    en: 'All severities',
    fun: 'Any severity',
  },
  'admin.detailsPanel.severityLabel': { en: 'Severity', fun: 'Severity' },
  'admin.detailsPanel.searchAriaLabel': {
    en: 'Search records',
    fun: 'Search records',
  },
  'admin.detailsPanel.searchPlaceholder': {
    en: 'Search…',
    fun: 'Dig around…',
  },
  'admin.detailsPanel.loading': { en: 'Loading…', fun: 'Digging…' },

  // ---- dashboard/DrilldownTable.tsx ----
  'admin.drilldownTable.col.links': { en: 'Links', fun: 'Links' },
  'admin.drilldownTable.col.service': { en: 'Service', fun: 'Service' },
  'admin.drilldownTable.col.environment': {
    en: 'Environment',
    fun: 'Environment',
  },
  'admin.drilldownTable.col.sync': { en: 'Sync', fun: 'Sync' },
  'admin.drilldownTable.col.owner': { en: 'Owner', fun: 'Owner' },
  'admin.drilldownTable.col.issue': { en: 'Issue', fun: 'Issue' },
  'admin.drilldownTable.col.consumerGroup': {
    en: 'Consumer group',
    fun: 'Consumer group',
  },
  'admin.drilldownTable.col.topic': { en: 'Topic', fun: 'Topic' },
  'admin.drilldownTable.col.lag': { en: 'Lag', fun: 'Drag' },
  'admin.drilldownTable.col.culprit': { en: 'Culprit', fun: 'Culprit' },
  'admin.drilldownTable.col.workflow': { en: 'Workflow', fun: 'Workflow' },
  'admin.drilldownTable.col.branch': { en: 'Branch', fun: 'Branch' },
  'admin.drilldownTable.col.result': { en: 'Result', fun: 'Result' },
  'admin.drilldownTable.col.package': { en: 'Package', fun: 'Package' },
  'admin.drilldownTable.col.severity': { en: 'Severity', fun: 'Severity' },
  'admin.drilldownTable.col.project': { en: 'Project', fun: 'Project' },
  'admin.drilldownTable.col.gate': { en: 'Gate', fun: 'Gate' },
  'admin.drilldownTable.col.type': { en: 'Type', fun: 'Type' },
  'admin.drilldownTable.col.detail': { en: 'Detail', fun: 'Detail' },
  'admin.drilldownTable.badge.outOfSync': {
    en: 'Out of sync',
    fun: 'Out of step',
  },
  'admin.drilldownTable.badge.degraded': { en: 'Degraded', fun: 'Wobbling' },
  'admin.drilldownTable.badge.podFailed': {
    en: 'Pod failed',
    fun: 'Pod down',
  },
  'admin.drilldownTable.badge.failed': { en: 'Failed', fun: 'Belly flopped' },
  'admin.drilldownTable.badge.success': { en: 'Success', fun: 'Nailed it' },
  'admin.drilldownTable.badge.cancelled': {
    en: 'Cancelled',
    fun: 'Called off',
  },
  'admin.drilldownTable.badge.skipped': { en: 'Skipped', fun: 'Skipped' },
  'admin.drilldownTable.badge.timedOut': {
    en: 'Timed out',
    fun: 'Fell asleep',
  },
  'admin.drilldownTable.badge.passed': { en: 'Passed', fun: 'Aced it' },
  'admin.drilldownTable.link.catalogFallback': {
    en: 'Catalog ↗',
    fun: 'Catalog ↗',
  },
  'admin.drilldownTable.link.openExternal': { en: 'Open ↗', fun: 'Peek ↗' },
  'admin.drilldownTable.link.kafkaFallback': { en: 'Kafka', fun: 'Kafka' },
  'admin.drilldownTable.link.kafkaUiExternal': {
    en: 'Kafka UI ↗',
    fun: 'Kafka UI ↗',
  },
  'admin.drilldownTable.emptyText': {
    en: 'No failing records — all clear.',
    fun: 'Nothing failing — smooth sailing.',
  },

  // ---- dashboard/KpiTile.tsx ----
  'admin.kpiTile.status.healthy': { en: 'Healthy', fun: 'Thriving' },
  'admin.kpiTile.status.warn': { en: 'Warning', fun: 'Side-eye' },
  'admin.kpiTile.status.error': { en: 'Critical', fun: 'On fire' },
  'admin.kpiTile.staleLabel': { en: 'stale', fun: 'stale' },
  'admin.kpiTile.staleSuffix': { en: '· stale', fun: '· stale' },
  'admin.kpiTile.loadingAriaLabel': { en: 'Loading', fun: 'Loading' },

  // ---- dashboard/ServiceHealthDonut.tsx ----
  'admin.healthDonut.healthy': { en: 'Healthy', fun: 'Thriving' },
  'admin.healthDonut.outOfSync': { en: 'Out of sync', fun: 'Out of step' },
  'admin.healthDonut.degraded': { en: 'Degraded', fun: 'Wobbling' },
  'admin.healthDonut.servicesLabel': { en: 'Services', fun: 'Services' },
  'admin.healthDonut.pctHealthy': {
    en: '{pct}% healthy',
    fun: '{pct}% thriving',
  },

  // ---- dashboard/IssuesByOwnerChart.tsx ----
  'admin.ownerChart.showingTopOf': {
    en: 'showing top {topN} of {total}',
    fun: 'showing the top {topN} of {total}',
  },

  // ---- assignments/AssignmentsSection.tsx (brand: "Merge Lord" identical) ----
  'admin.assignments.toast.added': {
    en: 'Added {name} as Merge Lord',
    fun: 'Crowned {name} as Merge Lord',
  },
  'admin.assignments.toast.addError': {
    en: 'Could not add Merge Lord',
    fun: 'Could not crown a new Merge Lord',
  },
  'admin.assignments.toast.toggleError': {
    en: 'Could not update Merge Lord',
    fun: 'Could not update Merge Lord',
  },
  'admin.assignments.toast.removed': {
    en: 'Removed Merge Lord',
    fun: 'Dethroned a Merge Lord',
  },
  'admin.assignments.toast.removeError': {
    en: 'Could not remove Merge Lord',
    fun: 'Could not dethrone Merge Lord',
  },

  // ---- assignments/AssigneeSearch.tsx ----
  'admin.assigneeSearch.placeholder': {
    en: 'Search people by name…',
    fun: 'Hunt for a human by name…',
  },
  'admin.assigneeSearch.ariaLabel': { en: 'Assignee', fun: 'Assignee' },
  'admin.assigneeSearch.loading': { en: 'Loading…', fun: 'Scouting…' },
  'admin.assigneeSearch.noMatches': { en: 'No matches', fun: 'Nobody found' },

  // ---- assignments/CharacterCard.tsx (brand: "Merge Lord(s)" identical) ----
  'admin.characterCard.openAriaLabel': {
    en: 'Open {title}',
    fun: 'Open {title}',
  },
  'admin.characterCard.mergeLordSingular': {
    en: '{count} Merge Lord',
    fun: '{count} Merge Lord',
  },
  'admin.characterCard.mergeLordPlural': {
    en: '{count} Merge Lords',
    fun: '{count} Merge Lords',
  },
  'admin.characterCard.unassigned': {
    en: 'Unassigned · tap to staff',
    fun: 'Throne empty · tap to fill it',
  },

  // ---- assignments/MergeLordDetailView.tsx (brand: "Merge Lord" identical) ----
  'admin.mergeLordDetail.backLink': {
    en: 'Back to characters',
    fun: 'Back to the roster',
  },
  'admin.mergeLordDetail.rosterTitle': {
    en: 'Merge Lords',
    fun: 'Merge Lords',
  },
  'admin.mergeLordDetail.addLabel': {
    en: 'Add a Merge Lord',
    fun: 'Recruit a Merge Lord',
  },
  'admin.mergeLordDetail.addingButton': { en: 'Adding…', fun: 'Recruiting…' },
  'admin.mergeLordDetail.addButton': { en: 'Add', fun: 'Recruit' },
  'admin.mergeLordDetail.prsTitle': {
    en: 'Merge Lord pull requests',
    fun: 'Merge Lord pull requests',
  },
  'admin.mergeLordDetail.taggedLabel': {
    en: 'tagged merge-lord-on-mission',
    fun: 'tagged merge-lord-on-mission',
  },
  'admin.mergeLordDetail.refreshPrsTooltip': {
    en: 'Refresh pull requests',
    fun: 'Fetch fresh PRs',
  },

  // ---- assignments/RosterList.tsx (brand: "Merge Lord" identical) ----
  'admin.rosterList.empty': {
    en: 'No Merge Lords yet — add someone below.',
    fun: 'No Merge Lords yet — add someone below.',
  },
  'admin.rosterList.activeLabel': { en: 'active', fun: 'on duty' },
  'admin.rosterList.inactiveLabel': { en: 'inactive', fun: 'benched' },
  'admin.rosterList.deactivateAriaLabel': {
    en: 'Deactivate {name}',
    fun: 'Bench {name}',
  },
  'admin.rosterList.reactivateAriaLabel': {
    en: 'Reactivate {name}',
    fun: 'Un-bench {name}',
  },
  'admin.rosterList.deactivateButton': { en: 'Deactivate', fun: 'Bench' },
  'admin.rosterList.reactivateButton': { en: 'Reactivate', fun: 'Un-bench' },
  'admin.rosterList.removeAriaLabel': {
    en: 'Remove {name}',
    fun: 'Kick {name} out',
  },
  'admin.rosterList.removeButton': { en: 'Remove', fun: 'Kick out' },
  'admin.rosterList.tableAriaLabel': {
    en: 'Merge Lord roster',
    fun: 'Merge Lord roster',
  },

  // ---- assignments/StalePrTable.tsx ----
  'admin.stalePrTable.emptyDefault': {
    en: 'No stale pull requests.',
    fun: 'Nothing stale — everything is fresh.',
  },
  'admin.stalePrTable.loading': {
    en: 'Loading stale pull requests…',
    fun: 'Sniffing out stale PRs…',
  },
  'admin.stalePrTable.staleDaysLabel': {
    en: '{days}d stale',
    fun: '{days}d and ripening',
  },
  'admin.stalePrTable.tableAriaLabel': {
    en: 'Stale pull requests',
    fun: 'Stale pull requests',
  },

  // ---- cron/CronJobsSection.tsx ----
  'admin.cron.tab.trigger': { en: 'Trigger', fun: 'Trigger' },
  'admin.cron.tab.runs': { en: 'Runs', fun: 'Runs' },
  'admin.cron.tabsAriaLabel': {
    en: 'Cron job sub-sections',
    fun: 'Cron job sub-sections',
  },
  'admin.cron.toast.ranSuccess': {
    en: '{job} ran successfully',
    fun: '{job} nailed it',
  },
  'admin.cron.toast.runError': {
    en: 'Could not run {job}',
    fun: '{job} refused to run',
  },
  'admin.cron.runSummary': {
    en: 'labeled {labeled} · unlabeled {unlabeled} · skipped {skipped}',
    fun: 'tagged {labeled} · untagged {unlabeled} · skipped {skipped}',
  },
  'admin.cron.counter.checked': { en: 'checked', fun: 'peeked at' },
  'admin.cron.counter.pruned': { en: 'pruned', fun: 'yeeted' },
  'admin.cron.counter.kept': { en: 'kept', fun: 'spared' },

  // ---- cron/CronRunsView.tsx ----
  'admin.cronRuns.allJobsOption': { en: 'All jobs', fun: 'Every job' },
  'admin.cronRuns.allStatusesOption': {
    en: 'All statuses',
    fun: 'Any status',
  },
  'admin.cronRuns.statusOk': { en: 'OK', fun: 'OK' },
  'admin.cronRuns.statusError': { en: 'Error', fun: 'Oops' },
  'admin.cronRuns.jobLabel': { en: 'Job', fun: 'Job' },
  'admin.cronRuns.statusLabel': { en: 'Status', fun: 'Status' },
  'admin.cronRuns.loading': {
    en: 'Loading cron runs…',
    fun: 'Rewinding the tape…',
  },
  'admin.cronRuns.empty': {
    en: 'No cron runs match the current filters.',
    fun: 'Nothing matches those filters.',
  },
  'admin.cronRuns.tableAriaLabel': { en: 'Cron runs', fun: 'Cron runs' },

  // ---- cron/CronTriggerView.tsx ----
  'admin.cronTrigger.neverRun': { en: 'Never run', fun: 'Never woken up' },
  'admin.cronTrigger.loading': {
    en: 'Loading jobs…',
    fun: 'Waking up the jobs…',
  },
  'admin.cronTrigger.empty': {
    en: 'No cron jobs registered.',
    fun: 'No robots on the clock.',
  },
  'admin.cronTrigger.runningButton': { en: 'Running…', fun: 'Zooming…' },
  'admin.cronTrigger.runNowButton': { en: 'Run now', fun: 'Go now' },

  // ---- dashboard/platform/PlatformHealthView.tsx ----
  'admin.platformHealth.title': { en: 'Platform Health', fun: 'Vitals' },
  'admin.platformHealth.subtitle': {
    en: 'Backend process, database and cache diagnostics',
    fun: 'What is going on under the hood',
  },
  'admin.platformHealth.error': {
    en: 'Could not load platform health',
    fun: 'Could not take its pulse',
  },
  'admin.platformHealth.staleNotice': {
    en: "Couldn't refresh",
    fun: 'Missed a beat',
  },
  'admin.platformHealth.refreshTooltip': {
    en: 'Refresh platform health',
    fun: 'Refresh the Vitals',
  },

  // ---- dashboard/platform/ProcessHealthCard.tsx ----
  'admin.platformHealth.process.title': { en: 'Process', fun: 'Engine Room' },
  'admin.platformHealth.process.rss': { en: 'RSS', fun: 'RSS' },
  'admin.platformHealth.process.heap': { en: 'Heap', fun: 'Heap' },
  'admin.platformHealth.process.cpu': { en: 'CPU', fun: 'CPU' },
  'admin.platformHealth.process.external': { en: 'External', fun: 'External' },
  'admin.platformHealth.process.uptime': { en: 'Uptime', fun: 'Awake for' },
  'admin.platformHealth.process.node': { en: 'Node', fun: 'Node' },

  // ---- dashboard/platform/DbStorageCard.tsx ----
  'admin.platformHealth.db.title': { en: 'Database Storage', fun: 'Byte Cellar' },
  'admin.platformHealth.db.total': { en: 'Total size', fun: 'Total size' },
  'admin.platformHealth.db.table': { en: 'Table', fun: 'Table' },
  'admin.platformHealth.db.size': { en: 'Size', fun: 'Size' },
  'admin.platformHealth.db.naNote': {
    en: 'Size unavailable for this database',
    fun: 'This database keeps its weight a secret',
  },

  // ---- dashboard/platform/CacheCard.tsx ----
  'admin.platformHealth.cache.title': { en: 'Caches', fun: 'Stashes' },
  'admin.platformHealth.cache.name': { en: 'Cache', fun: 'Stash' },
  'admin.platformHealth.cache.entries': { en: 'Entries', fun: 'Entries' },
  'admin.platformHealth.cache.ttl': { en: 'TTL', fun: 'TTL' },
  'admin.platformHealth.cache.nextExpiry': {
    en: 'Next expiry',
    fun: 'Next to go stale',
  },
  'admin.platformHealth.cache.key': { en: 'Key', fun: 'Key' },
  'admin.platformHealth.cache.age': { en: 'Age', fun: 'Age' },
  'admin.platformHealth.cache.expires': { en: 'Expires', fun: 'Expires' },
  'admin.platformHealth.cache.empty': {
    en: 'No caches reporting yet.',
    fun: 'No stashes to peek into yet.',
  },
  'admin.platformHealth.cache.searchPlaceholder': {
    en: 'Search caches or keys…',
    fun: 'Snoop for a stash or key…',
  },
  'admin.platformHealth.cache.searchAriaLabel': {
    en: 'Search caches by name or entry key',
    fun: 'Snoop stashes by name or entry key',
  },
  'admin.platformHealth.cache.noMatches': {
    en: 'No caches match that search.',
    fun: 'Tumbleweeds. No stashes match that.',
  },
};
